import { Input } from 'antd';
import type { InsuredTableData, Status } from './types';
import StatusIndicator from 'components/ui/status-indicator';
import CustomButton from 'components/ui/custom-button';
import { EButtonVariant } from 'components/ui/custom-button/enums';
import CustomTable from 'components/ui/custom-table';
import type { TableColumnsType } from 'antd';
import { AddIcon } from 'assets/icons/add';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import { useQuery } from 'react-query';
import usePagination from 'hooks/usePagination';
import { useMemo, useState } from 'react';
import ContentIcon from 'assets/icons/content';
import { insuredApi } from 'services/apis';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'assets/icons/search';
import { EQueryKeys } from 'constants/query-keys';

export default function InsuredTable() {
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination();

  const { t } = useTranslation();

  const {
    data: { items: insuredData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
  } = useQuery(
    [EQueryKeys.INSURED_LIST, pageSize, currentPage, searchText],
    async () => {
      const { items, pagination } = await insuredApi.filterInsureds({
        page: currentPage,
        pageSize,
        insuredFilterQueryDto: {
          searchText,
        },
      });
      return {
        items: items.map((insured, index) => ({
          index: pageSize * (currentPage - 1) + index,
          key: insured.insuredId,
          name: insured.name,
          country: insured.country,
          sanctionsCompleted: insured.sanctionsCompleted,
          sanctionsCompletedDate: insured.sanctionsCompletedDate,
          status: insured.status,
          insuredId: insured.insuredId,
        })),
        pagination,
      };
    }
  );
  const navigate = useNavigate();
  const handleSearchTrigger = () => setSearchText(inputText);

  const columns: TableColumnsType<InsuredTableData> = useMemo(
    () => [
      {
        key: 'index',
        title: <ContentIcon />,
        dataIndex: 'index',
        width: '1%',
        align: 'center',
        render: (index: number) => index + 1,
      },
      {
        key: 'name',
        title: t('insured.insuredName'),
        dataIndex: 'name',
        width: '25%',
        align: 'left',
        render: (text: string) => (
          <div
            style={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxWidth: '25vw',
            }}
          >
            {text}
          </div>
        ),
      },
      {
        key: 'country',
        title: t('insured.country'),
        dataIndex: 'country',
        width: '20%',
        align: 'left',
      },
      {
        key: 'sanctionsCompleted',
        title: t('insured.sanctionsCompleted'),
        dataIndex: 'sanctionsCompleted',
        width: '15%',
        align: 'left',
      },
      {
        key: 'sanctionsCompletedDate',
        title: t('insured.sanctionsLastDate'),
        dataIndex: 'sanctionsCompletedDate',
        width: '15%',
        align: 'left',
      },
      {
        key: 'status',
        title: t('general.status'),
        dataIndex: 'status',
        width: '15%',
        align: 'left',
        render: (text: Status) => <StatusIndicator status={text} />,
      },
    ],
    [t]
  );

  return (
    <div className="w-full py-4">
      <div className="flex justify-between mb-4 items-center">
        <div className="text-2xl font-medium">{t('insured.selectInsured')}</div>
        <CustomButton
          className="w-1/5"
          variant={EButtonVariant.PRIMARY}
          label="Create New Record"
          prefixIcon={<AddIcon />}
          onClick={() => navigate(ENavigationPath.NEW_INSURED)}
        />
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Input
          placeholder="Search Insured if exists"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          className="flex-grow py-[8px]"
          prefix={<SearchIcon />}
          onPressEnter={handleSearchTrigger}
        />
        <CustomButton
          className="w-2/12 py-[8px]"
          variant={EButtonVariant.PRIMARY}
          label={t('general.search')}
          onClick={handleSearchTrigger}
        />
      </div>

      <CustomTable
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onShowSizeChange={onShowSizeChange}
        columns={columns}
        dataSource={insuredData || []}
        total={total || 0}
        onRow={(record) => ({
          onClick: () =>
            navigate(
              ENavigationPath.UPDATE_INSURED.replace(':id', record.insuredId)
            ),
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />
    </div>
  );
}
